import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiFailure } from "./client";

/**
 * usePagedResource carga una lista paginada por cursor.
 *
 * La API pagina por cursor y no por número de página (CLAUDE.md §6): devuelve
 * `meta.next_cursor` y acepta `?cursor=`. Un cursor sabe ir hacia adelante y
 * **no** sabe ir hacia atrás, así que para que el botón «Anterior» funcione
 * este hook guarda la pila de cursores por los que ha pasado. Retroceder es
 * sacar uno de la pila y volver a pedir.
 *
 * Por qué cursor y no `?page=3`: con `OFFSET`, insertar una fila mientras
 * alguien pagina desplaza todo y hace que una fila se vea dos veces o ninguna.
 * En una lista de llamadas, que crece mientras se mira, eso pasa siempre.
 *
 * El precio honesto es que no se puede decir «página 3 de 47»: para saber
 * cuántas hay habría que contarlas todas en cada petición. Se dice «hay más»,
 * que es lo que la API sabe.
 */
export type PagedResource<T> = {
  /** La página actual. `null` mientras carga por primera vez. */
  data: T[] | null;
  error: string | null;
  loading: boolean;
  /** Recarga la página actual, sin moverse. */
  reload: (silent?: boolean) => Promise<void>;
  hayMas: boolean;
  hayAnterior: boolean;
  siguiente: () => void;
  anterior: () => void;
  /** Vuelve a la primera página. Para cuando cambia un filtro. */
  reiniciar: () => void;
  /** Cuántos elementos trae la página actual. */
  enEstaPagina: number;
  /** Hay una recarga silenciosa en vuelo. Para el aviso de «actualizando». */
  refrescando: boolean;
};

export function usePagedResource<T>(
  /** Ruta sin cursor. Puede traer su propia query: se añade con `&`. */
  path: string | null,
  limite = 25,
): PagedResource<T> {
  const [data, setData] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [siguienteCursor, setSiguienteCursor] = useState<string | null>(null);
  const [refrescando, setRefrescando] = useState(false);
  // La pila no va en el estado: cambiarla no tiene que repintar, y si fuera
  // estado entraría en las dependencias de `reload` y provocaría una recarga
  // cada vez que se avanza, además de la que ya hace el cambio de cursor.
  const pila = useRef<string[]>([]);

  const reload = useCallback(
    async (silent = false) => {
      if (path === null) return;
      if (!silent) {
        setData(null);
        setError(null);
      } else {
        setRefrescando(true);
      }
      const separador = path.includes("?") ? "&" : "?";
      const conCursor = `${path}${separador}limit=${limite}${
        cursor === null ? "" : `&cursor=${encodeURIComponent(cursor)}`
      }`;
      try {
        const pagina = await api.getPage<T[]>(conCursor);
        setData(pagina.data ?? []);
        setSiguienteCursor(pagina.nextCursor ?? null);
        setError(null);
      } catch (err) {
        setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
      } finally {
        setRefrescando(false);
      }
    },
    [path, limite, cursor],
  );

  useEffect(() => {
    void reload(false);
  }, [reload]);

  // Cuando cambia la ruta —un filtro nuevo— hay que volver al principio: un
  // cursor de la lista anterior no significa nada en la nueva.
  useEffect(() => {
    pila.current = [];
    setCursor(null);
    setSiguienteCursor(null);
  }, [path]);

  const siguiente = useCallback(() => {
    if (siguienteCursor === null) return;
    pila.current = [...pila.current, cursor ?? ""];
    setCursor(siguienteCursor);
  }, [siguienteCursor, cursor]);

  const anterior = useCallback(() => {
    const previo = pila.current[pila.current.length - 1];
    if (previo === undefined) return;
    pila.current = pila.current.slice(0, -1);
    setCursor(previo === "" ? null : previo);
  }, []);

  const reiniciar = useCallback(() => {
    pila.current = [];
    setCursor(null);
  }, []);

  return {
    data,
    error,
    loading: data === null && error === null,
    reload,
    hayMas: siguienteCursor !== null,
    hayAnterior: pila.current.length > 0,
    siguiente,
    anterior,
    reiniciar,
    enEstaPagina: data?.length ?? 0,
    refrescando,
  };
}
